import React, { useState } from "react";
import { ImageUpload } from "@/components/molecules/ImageUpload";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, XCircle } from "lucide-react";

interface ImageUploadSectionProps {
  currentImage?: string | null;
  onImageUpload?: (file: File) => Promise<void>;
  imageLoading?: boolean;
}

export const ImageUploadSection: React.FC<ImageUploadSectionProps> = ({
  currentImage,
  onImageUpload,
  imageLoading = false,
}) => {
  const [imageUploadSuccess, setImageUploadSuccess] = useState<string | null>(null);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);

  const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleImageUpload = async (file: File) => {
    if (!onImageUpload) return;

    setImageUploadSuccess(null);
    setImageUploadError(null);

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setImageUploadError("Invalid file format. Only JPG and PNG files are allowed.");
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setImageUploadError("File size too large. Maximum allowed size is 5MB.");
      return;
    }

    try {
      await onImageUpload(file);
      setImageUploadSuccess("Profile image updated successfully!");

      setTimeout(() => {
        setImageUploadSuccess(null);
      }, 3000);
    } catch (error) {
      console.error("Image upload error:", error);
      setImageUploadError("Failed to upload image. Please try again.");
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-foreground">
        Profile Image
      </h3>

      {/* Image Upload Success Message */}
      {imageUploadSuccess && (
        <Alert className="border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20">
          <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            {imageUploadSuccess}
          </AlertDescription>
        </Alert>
      )}

      {/* Image Upload Error Message */}
      {imageUploadError && (
        <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
          <AlertDescription className="text-red-800 dark:text-red-200">
            {imageUploadError}
          </AlertDescription>
        </Alert>
      )}

      <ImageUpload
        currentImage={currentImage}
        onImageSelect={handleImageUpload}
        loading={imageLoading}
        error={imageUploadError}
        label="Business Profile Image"
        description="Upload a logo or profile image for your business"
        accept="image/jpeg,image/png"
        allowedFormatsText="JPG, PNG"
      />
    </div>
  );
};